<template>
  <div class="page-report">
    <HeaderMegaposData />
    <div class="page-header align-items-start min-vh-100 my-transacions">
      <span class="mask bg-white"></span>
      <div class="container">
        <div class="row">
          <div class="col-lg-12 col-md-12 col-12 mx-auto mt-4">
            <div class="d-flex align-items-center gap-2 flex-wrap">
              <button type="button" class="btn btn-link mb-0 px-0" @click="$router.push({ name: 'ReportBuilderList' })">← {{ $t("rb_back") }}</button>
              <h2 class="mb-0">{{ report ? report.name : "" }}</h2>
              <material-button
                v-if="report"
                variant="outline"
                color="info"
                size="sm"
                class="mb-0 ms-auto"
                @click="$router.push({ name: 'ReportBuilderEdit', params: { id: report.id } })"
              >{{ $t("rb_edit") }}</material-button>
            </div>
            <!-- The same filter as the fixed reports (Item Sales Detail): physical branch, whole days. -->
            <FormSearch
              :searchParameter="form"
              :onChangeSearchParameter="onChangeSearchParameter"
              :selectAllBranch="false"
              :hidePhysicalBranchFilter="false"
              :selectAllPhysicalBranch="true"
              :totalDaySelect="31"
              :exportTypes="['csv']"
              @reload-data="run"
              @export="download"
            ></FormSearch>

            <div class="rb-panel rb-results">
              <div class="rb-note-line">
                <span class="material-icons-round">schedule</span>
                {{ $t("rb_closed_note") }}
              </div>
              <div class="p-3">
                <div v-if="running" class="rb-empty"><span class="spinner-border spinner-border-sm text-info"></span></div>
                <div v-else-if="errorMessage" class="rb-empty">{{ errorMessage }}</div>
                <report-result-table v-else-if="result" :result="result" />
                <div v-else class="rb-empty">{{ $t("rb_pick_and_run") }}</div>
              </div>
              <div v-if="result && !running" class="rb-status">
                <span>{{ $t("rb_rows", { n: result.rows.length.toLocaleString() }) }}</span>
                <span>{{ elapsed }} ms</span>
                <span>{{ result.from }} → {{ result.to }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <form :action="`${actionDownload}/merchant/analytics/report-builder/export`" method="POST">
      <input type="hidden" name="merchantId" :value="merchantIdExport" />
      <input type="hidden" name="merchant_code" :value="merchantCode" />
      <input type="hidden" name="token" :value="token" />
      <input type="hidden" name="id" :value="report ? report.id : ''" />
      <input type="hidden" name="physical_branch_id" :value="form.physical_branch_id" />
      <input type="hidden" name="branch_id" value="-1" />
      <input type="hidden" name="from_date" :value="day(form.from_date)" />
      <input type="hidden" name="to_date" :value="day(form.to_date)" />
      <input id="downloadFormReportBuilder" type="submit" class="d-none" />
    </form>

    <div class="position-fixed top-1 end-1 z-index-2 message-success">
      <material-snackbar
        v-if="snackbar === 'danger' || snackbar === 'success'"
        title=""
        date=""
        :description="message"
        :icon="{ component: 'campaign', color: 'white' }"
        :color="snackbar"
        :close-handler="() => (snackbar = null)"
      />
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { startOfMonth } from "date-fns";
import MaterialButton from "@/components/MaterialButton.vue";
import MaterialSnackbar from "@/components/MaterialSnackbar.vue";
import AnalyticsService from "../../../services/analytics.service";
import TokenService from "../../../services/token.service";
import FormSearch from "../components/FormSearch.vue";
import HeaderMegaposData from "../components/HeaderMegaposData.vue";
import ReportResultTable from "./ReportResultTable.vue";

export default {
  name: "ReportBuilderView",
  components: { MaterialButton, MaterialSnackbar, FormSearch, HeaderMegaposData, ReportResultTable },
  data() {
    return {
      report: null,
      // FormSearch's own defaults: this month so far, every physical branch ("ALL" = 0).
      form: {
        physical_branch_id: 0,
        branch_id: -1,
        from_date: moment(startOfMonth(new Date())),
        to_date: new Date(),
      },
      result: null,
      running: false,
      errorMessage: "",
      elapsed: 0,
      token: TokenService.getLocalAccessToken(),
      actionDownload: process.env.VUE_APP_BASE_URL,
      merchantIdExport: localStorage.getItem("MERCHANT_ID", ""),
      merchantCode: localStorage.getItem("MERCHANT_CODE", ""),
      snackbar: null,
      message: "",
    };
  },
  async created() {
    // Nothing runs until Run is pressed, as on the fixed reports.
    try {
      const detail = await AnalyticsService.reportBuilderDetail(this.$route.params.id);
      if (detail.data.result.isSuccess) {
        this.report = detail.data.data.report;
      } else {
        this.errorMessage = detail.data.result.message;
      }
    } catch (e) {
      this.errorMessage = this.$t("rb_load_failed");
    }
  },
  methods: {
    onChangeSearchParameter(value, fieldName) {
      this.form[fieldName] = value !== null ? value : "-1";
    },
    // Whole days, as the fixed reports send them (the API reads a bare date as the whole day).
    day(date) {
      return date ? moment(date).format("YYYY-MM-DD") : "";
    },
    async run() {
      if (!this.report) return;
      this.running = true;
      this.errorMessage = "";
      const started = Date.now();
      try {
        const res = await AnalyticsService.reportBuilderRun({
          id: this.report.id,
          from_date: this.day(this.form.from_date),
          to_date: this.day(this.form.to_date),
          physical_branch_id: this.form.physical_branch_id,
          branch_id: -1,
        });
        if (res.data.result.isSuccess) {
          this.result = res.data.data;
        } else {
          this.result = null;
          this.errorMessage = res.data.result.message;
        }
      } catch (e) {
        this.result = null;
        this.errorMessage = this.$t("rb_run_failed");
      } finally {
        this.elapsed = Date.now() - started;
        this.running = false;
      }
    },
    download() {
      if (!this.report) return;
      // A form POST like the other report exports: the browser saves the CSV itself.
      this.$nextTick(() => document.getElementById("downloadFormReportBuilder").click());
    },
  },
};
</script>

<style scoped>
.rb-panel {
  background: #fff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
}
/* Below FormSearch's export buttons, which it positions absolutely. */
.rb-results {
  margin-top: 72px;
}
.rb-note-line {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 8px 16px;
  font-size: 0.75rem;
  color: #495057;
  background: #f5f7f7;
}
.rb-note-line .material-icons-round {
  font-size: 1rem;
}
.rb-status {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  padding: 8px 16px;
  font-size: 0.75rem;
  color: #6c757d;
  border-top: 1px solid #eef0f0;
}
.rb-empty {
  padding: 32px 12px;
  text-align: center;
  color: #6c757d;
}
</style>
